import { useEffect, useState, useMemo } from "react";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DateTimeValidationError } from "@mui/x-date-pickers/models";
import { InfoOutlined } from "@mui/icons-material";
import {
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  Tooltip,
  Zoom,
} from "@mui/material";
import { Dayjs } from "dayjs";

import { DeviceRangeReadingsGranularity } from "../../api/types/devices";
import classes from "./index.module.scss";

type Props = {
  onChange: (
    from: string,
    to: string,
    granularity: DeviceRangeReadingsGranularity
  ) => void;
};

const DateTimeRangePicker = ({ onChange }: Props) => {
  const [fromDateTime, setFromDateTime] = useState<Dayjs | null>(null);
  const [toDateTime, setToDateTime] = useState<Dayjs | null>(null);
  const [granularity, setGranularity] =
    useState<DeviceRangeReadingsGranularity>("5m");
  const [fromError, setFromError] = useState<DateTimeValidationError | null>(
    null
  );
  const [toError, setToError] = useState<DateTimeValidationError | null>(null);

  useEffect(() => {
    if (fromDateTime && toDateTime && fromError === null && toError === null) {
      onChange(
        fromDateTime.toISOString(),
        toDateTime.toISOString(),
        granularity
      );
    }
  }, [fromDateTime, toDateTime, granularity, fromError, toError]);

  const fromErrorMessage = useMemo(() => {
    switch (fromError) {
      case "maxTime":
      case "maxDate": {
        return "Please select a date lower than End date";
      }
      case "invalidDate": {
        return "Your date is not valid";
      }
      case "disableFuture": {
        return "Can't select a future date"
      }
      default: {
        return "";
      }
    }
  }, [fromError]);

  const toErrorMessage = useMemo(() => {
    switch (toError) {
      case "minTime":
      case "minDate": {
        return "Please select a date higher than Start date";
      }
      case "invalidDate": {
        return "Your date is not valid";
      }
      case "disableFuture": {
        return "Can't select a future date"
      }
      default: {
        return "";
      }
    }
  }, [toError]);

  return (
    <div className={classes.container}>
      <div className={classes.dateTimeRangeContainer}>
        <DateTimePicker
          label="Start"
          ampm={false}
          value={fromDateTime}
          onChange={setFromDateTime}
          maxDateTime={toDateTime || undefined}
          onError={setFromError}
          slotProps={{
            textField: {
              helperText: fromErrorMessage,
            },
            actionBar: {
              actions: ["today"],
            },
          }}
          disableFuture
        />
        to
        <DateTimePicker
          label="End"
          ampm={false}
          value={toDateTime}
          onChange={setToDateTime}
          minDateTime={fromDateTime || undefined}
          onError={setToError}
          slotProps={{
            textField: {
              helperText: toErrorMessage,
            },
            actionBar: {
              actions: ["today"],
            },
          }}
          disableFuture
        />
      </div>
      <FormControl sx={{ m: 1, width: 180 }}>
        <Select
          labelId="demo-simple-select-filled-label"
          id="demo-simple-select-filled"
          value={granularity}
          onChange={(v) =>
            setGranularity(v.target.value as DeviceRangeReadingsGranularity)
          }
        >
          <MenuItem value="1m">1 minute</MenuItem>
          <MenuItem value="5m">5 minutes</MenuItem>
          <MenuItem value="20m">20 minutes</MenuItem>
          <MenuItem value="1d">1 day</MenuItem>
        </Select>
        <FormHelperText>
          Granularity{" "}
          <Tooltip
            TransitionComponent={Zoom}
            title="'1 minute' and '5 minutes' return data up to 3 months from now"
          >
            <InfoOutlined sx={{ fontSize: 14 }} />
          </Tooltip>
        </FormHelperText>
      </FormControl>
    </div>
  );
};

export default DateTimeRangePicker;
